import { useState, useEffect, useCallback } from 'react';
import { Trophy, CheckCircle, ChevronDown } from 'lucide-react';
import { trackWeeklyGoalComplete } from '@/lib/analytics';

const GOAL_KEY = 'mw-weekly-goal';
const READS_KEY = 'mw-weekly-reads';

type GoalTier = 3 | 5 | 10;
const TIERS: { value: GoalTier; label: string }[] = [
  { value: 3, label: 'Easy — 3/week' },
  { value: 5, label: 'Medium — 5/week' },
  { value: 10, label: 'Hard — 10/week' },
];

function getWeekKey(): string {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((day + 6) % 7));
  return monday.toISOString().slice(0, 10);
}

function getSavedGoal(): GoalTier {
  try {
    const v = Number(localStorage.getItem(GOAL_KEY));
    if (v === 3 || v === 5 || v === 10) return v;
  } catch { /* ignore */ }
  return 5;
}

function getWeeklyReads(): number {
  try {
    const raw = localStorage.getItem(READS_KEY);
    if (!raw) return 0;
    const data = JSON.parse(raw) as { week: string; count: number };
    if (data.week !== getWeekKey()) return 0;
    return data.count;
  } catch {
    return 0;
  }
}

/** Call this alongside incrementDailyRead to also bump the weekly counter */
export function incrementWeeklyRead(): void {
  const week = getWeekKey();
  const current = getWeeklyReads();
  localStorage.setItem(READS_KEY, JSON.stringify({ week, count: current + 1 }));
  window.dispatchEvent(new Event('weekly-read-update'));
}

// SVG circular progress ring
function ProgressRing({ pct, done, size = 80 }: { pct: number; done: boolean; size?: number }) {
  const stroke = 6;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        className="text-bg-tertiary"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className={`transition-all duration-700 ${done ? 'text-emerald-500' : 'text-indigo-500'}`}
        stroke="currentColor"
      />
    </svg>
  );
}

export function WeeklyGoalWidget() {
  const [goal, setGoal] = useState<GoalTier>(getSavedGoal);
  const [reads, setReads] = useState(getWeeklyReads);
  const [showPicker, setShowPicker] = useState(false);
  const [celebrated, setCelebrated] = useState(false);

  useEffect(() => {
    const handler = () => setReads(getWeeklyReads());
    window.addEventListener('weekly-read-update', handler);
    window.addEventListener('daily-read-update', handler);
    return () => {
      window.removeEventListener('weekly-read-update', handler);
      window.removeEventListener('daily-read-update', handler);
    };
  }, []);

  const progress = Math.min(reads, goal);
  const done = progress >= goal;
  const pct = Math.round((progress / goal) * 100);

  // Fire analytics + celebration on first completion
  useEffect(() => {
    if (done && !celebrated) {
      setCelebrated(true);
      trackWeeklyGoalComplete(goal, reads);
    }
  }, [done, celebrated, goal, reads]);

  // Reset celebration flag when week rolls over
  useEffect(() => {
    if (!done) setCelebrated(false);
  }, [done]);

  const changeGoal = useCallback((tier: GoalTier) => {
    localStorage.setItem(GOAL_KEY, String(tier));
    setGoal(tier);
    setShowPicker(false);
  }, []);

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-indigo-500/5 to-transparent">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500/10">
          {done ? (
            <CheckCircle className="h-4 w-4 text-emerald-500" />
          ) : (
            <Trophy className="h-4 w-4 text-indigo-500" />
          )}
        </div>
        <h3 className="font-semibold text-text-primary text-sm">This Week's Goal</h3>
      </div>

      {/* Content */}
      <div className="flex flex-col items-center px-4 py-4">
        {/* Progress ring with count overlay */}
        <div className="relative flex items-center justify-center">
          <ProgressRing pct={pct} done={done} />
          <span className="absolute text-lg font-bold tabular-nums text-text-primary">
            {progress}/{goal}
          </span>
        </div>

        <p className="mt-2 text-sm text-text-secondary text-center">
          {done
            ? 'Weekly goal crushed!'
            : `Read ${goal} articles this week`}
        </p>

        {/* Goal tier picker */}
        <button
          type="button"
          onClick={() => setShowPicker(!showPicker)}
          className="mt-2 flex items-center gap-1 text-xs text-text-tertiary hover:text-text-secondary transition-colors"
        >
          Change goal <ChevronDown className={`h-3 w-3 transition-transform ${showPicker ? 'rotate-180' : ''}`} />
        </button>

        {showPicker && (
          <div className="mt-2 flex flex-col gap-1 w-full">
            {TIERS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => changeGoal(t.value)}
                className={`rounded-lg px-3 py-1.5 text-xs text-left transition-colors ${
                  goal === t.value
                    ? 'bg-indigo-500/10 text-indigo-400 font-medium'
                    : 'text-text-tertiary hover:bg-bg-tertiary'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
